import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import type { InventoryInformationEditView } from "@slfinrtl/ui-bff-routes"
import { stockStatusMessages } from "@slfinrtl/ui-bff-routes"
import { useMemo } from "react"
import { defineMessages, useIntl } from "react-intl"
import { commonMessages } from "src/common/messages/common.messages"
import { useConfig } from "src/hooks"
import type { InventoryInformationFormValues } from "../InventoryInformationFormValues"
import { inventoryAndIntakeFieldMessages } from "../common/inventoryAndIntakeField.messages"
import type { InventoryAndIntakeFields } from "../inventoryAndIntake.fields"
import { inventoryAndIntakeFieldNames } from "../inventoryAndIntake.fields"
import { useStockStatusOptions } from "./useStockStatusOptions/useStockStatusOptions"
import { useVehicleOriginOptions } from "./useVehicleOriginOptions"
import { useVehicleTypeOptions } from "./useVehicleTypeOptions"

const messages = defineMessages({
	notPossible: {
		defaultMessage: "Cannot be set for selected stock status: {vehicleType}",
		id: "inventoryAndIntake.intake.intakeDate.disabledHint",
	},
})

export const useInventoryAndIntakeFields = (
	values: Partial<InventoryInformationFormValues>,
	view: InventoryInformationEditView,
	isDraft: boolean,
): InventoryAndIntakeFields => {
	const { formatMessage } = useIntl()

	const vmsDataSource =
		values.dataSource === "PVMS" || values.dataSource === "ONE_VMS" ? values.dataSource : undefined

	const vehicleTypeOptions = useVehicleTypeOptions(vmsDataSource, view)
	const stockStatusOptions = useStockStatusOptions(values.vehicleType, vmsDataSource, isDraft, view)
	const vehicleOriginOptions = useVehicleOriginOptions(values, !!vmsDataSource)
	const { masterNumberChAvailable } = useConfig()
	const { distance } = useDealerContext()

	const fields = useMemo((): InventoryAndIntakeFields => {
		const isArrivalDateDisabled = values.stockStatus === "INCOMING"
		const shouldDisplayAvailableFromDate = values.stockStatus === "IN_USE"
		const shouldDisplayExpectedArrivalDate = values.stockStatus === "INCOMING"

		return {
			arrivalDate: {
				disabledHint:
					values.dataSource === "PVMS"
						? formatMessage(commonMessages.disabledForVmsVehicle, { value: "PVMS" })
						: values.dataSource === "ONE_VMS"
							? formatMessage(commonMessages.disabledForVmsVehicle, { value: "OneVMS" })
							: isArrivalDateDisabled
								? formatMessage(messages.notPossible, {
										vehicleType: formatMessage(stockStatusMessages.INCOMING),
									})
								: undefined,
				label: formatMessage(inventoryAndIntakeFieldMessages.arrivalDateLabel),
				maxDate: values.stockStatus === "IN_STOCK" ? new Date().toISOString().split("T")[0] : undefined,
				name: inventoryAndIntakeFieldNames.arrivalDateName,
				required: !isArrivalDateDisabled,
			},
			availableForCustomerFromDate: {
				label: formatMessage(inventoryAndIntakeFieldMessages.availableForCustomerFromDateLabel),
				minDate: new Date().toISOString().split("T")[0],
				name: inventoryAndIntakeFieldNames.availableForCustomerFromDateName,
			},
			availableFromDate: shouldDisplayAvailableFromDate
				? {
						label: formatMessage(inventoryAndIntakeFieldMessages.availableFromDateLabel),
						name: inventoryAndIntakeFieldNames.availableFromDateName,
					}
				: undefined,
			expectedArrivalDate: shouldDisplayExpectedArrivalDate
				? {
						label: formatMessage(inventoryAndIntakeFieldMessages.expectedArrivalDateLabel),
						name: inventoryAndIntakeFieldNames.expectedArrivalDateName,
					}
				: undefined,
			inventoryNumber: {
				debounced: isDraft ? 500 : undefined,
				label: formatMessage(inventoryAndIntakeFieldMessages.inventoryNumberLabel),
				name: inventoryAndIntakeFieldNames.inventoryNumberName,
				required: true,
				type: "text",
			},
			licensePlate: {
				debounced: 500,
				label: formatMessage(inventoryAndIntakeFieldMessages.licensePlateLabel),
				name: inventoryAndIntakeFieldNames.licensePlateName,
				type: "text",
			},
			masterNumberCh: masterNumberChAvailable
				? {
						debounced: isDraft ? 500 : undefined,
						label: formatMessage(inventoryAndIntakeFieldMessages.masterNumberChLabel),
						name: inventoryAndIntakeFieldNames.masterNumberChName,
						required: true,
						type: "text",
					}
				: undefined,
			mileageAccordingToPreviousOwner: {
				debounced: isDraft ? 500 : undefined,
				label: formatMessage(inventoryAndIntakeFieldMessages.mileageAccordingToPreviousOwnersLabel),
				name: inventoryAndIntakeFieldNames.mileageAccordingToPreviousOwnerName,
				type: "number",
				unit: distance,
				unitPosition: "suffix",
			},
			stockStatus: {
				label: formatMessage(inventoryAndIntakeFieldMessages.stockStatusLabel),
				name: inventoryAndIntakeFieldNames.stockStatusName,
				options: stockStatusOptions,
				required: true,
			},
			typeKeyNumber: {
				debounced: isDraft ? 500 : undefined,
				label: formatMessage(inventoryAndIntakeFieldMessages.typeKeyNumberLabel),
				name: inventoryAndIntakeFieldNames.typeKeyNumberName,
				required: false,
				type: "text",
			},
			vehicleOrigin: {
				label: formatMessage(inventoryAndIntakeFieldMessages.vehicleOriginLabel),
				name: inventoryAndIntakeFieldNames.vehicleOriginName,
				options: vehicleOriginOptions,
				required: true,
			},
			vehicleType: {
				label: formatMessage(inventoryAndIntakeFieldMessages.vehicleTypeLabel),
				name: inventoryAndIntakeFieldNames.vehicleTypeName,
				options: vehicleTypeOptions,
				required: true,
			},
		}
	}, [
		formatMessage,
		isDraft,
		values.dataSource,
		values.stockStatus,
		stockStatusOptions,
		vehicleTypeOptions,
		masterNumberChAvailable,
		distance,
		vehicleOriginOptions,
	])

	return fields
}
